/**
 * Tracking for waitlists the player has just explicitly left.
 *
 * Why it exists: leaving a waitlist is REST-then-Redux (waitlistService
 * DELETE, then `removeWaitlistEntry`). The engine, however, keeps
 * broadcasting the player's spot until the DELETE is processed server-side,
 * and `BROADCASTED_WAITLIST` / the connect-time `fetchMyWaitlists` snapshot
 * are authoritative — they call `replaceWaitlistEntries`, which rebuilds
 * `mySpotByGameId` from whatever the server sent. An in-flight broadcast
 * therefore re-adds the spot the player just removed, which flips the lobby
 * tile back to "joined" and hides the Join Waitlist banner (the player can no
 * longer rejoin from the lobby).
 *
 * Mirrors `recentlyExitedTables` (which solves the identical re-hydration
 * race for table subscriptions): on Leave we stamp the gameID, and the
 * waitlist socket sync drops the player's OWN re-added entry for any stamped
 * game until the server confirms the removal (a snapshot that no longer lists
 * them) or the grace window lapses. Other players' entries are untouched, so
 * the waitlist count stays accurate.
 */

import type { WaitlistEntry } from 'types/waitlist';

export const RECENTLY_LEFT_WAITLIST_GRACE_MS = 15000;

export const RECENTLY_LEFT_WAITLIST_STORAGE_KEY = 'recentlyLeftWaitlists';

type LeftStore = Record<string, number>;

const readStore = (): LeftStore => {
  try {
    const parsed = JSON.parse(localStorage.getItem(RECENTLY_LEFT_WAITLIST_STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = (store: LeftStore): void => {
  try {
    localStorage.setItem(RECENTLY_LEFT_WAITLIST_STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* localStorage unavailable — fail open; the next clean broadcast still
       reconciles state once the server-side DELETE lands. */
  }
};

const isFresh = (ts: unknown): ts is number =>
  typeof ts === 'number' && Date.now() - ts < RECENTLY_LEFT_WAITLIST_GRACE_MS;

/** Stamp a gameID as just-left so re-added broadcasts for it are suppressed. */
export function markRecentlyLeftWaitlist(gameId: string | number): void {
  const key = String(gameId);
  if (!key) return;
  const store = readStore();
  store[key] = Date.now();
  writeStore(store);
}

/** Drop the stamp — call on an explicit (re)join so the spot is honoured. */
export function clearRecentlyLeftWaitlist(gameId: string | number): void {
  const key = String(gameId);
  if (!key) return;
  const store = readStore();
  if (key in store) {
    delete store[key];
    writeStore(store);
  }
}

export function isRecentlyLeftWaitlist(gameId: string | number): boolean {
  return isFresh(readStore()[String(gameId)]);
}

/**
 * Given an incoming waitlist snapshot (broadcast or fetch), drop the current
 * player's own re-added entries for games they just left, so a lagging engine
 * snapshot can't resurrect a spot the player already removed.
 *
 * Self-healing: a stamped game whose snapshot NO LONGER contains the player's
 * entry means the server processed the DELETE — clear the stamp so a later
 * legitimate rejoin is honoured immediately. Expired stamps are pruned too.
 */
export function suppressLeftWaitlistReentries(
  entries: WaitlistEntry[],
  myGuid: string
): WaitlistEntry[] {
  const store = readStore();
  const stampedKeys = Object.keys(store);
  if (stampedKeys.length === 0) return entries;

  const myGamesInSnapshot = new Set(
    entries.filter((entry) => entry.playerGuid === myGuid).map((entry) => String(entry.gameId))
  );

  const suppress = new Set<string>();
  let storeChanged = false;
  for (const key of stampedKeys) {
    if (!isFresh(store[key]) || !myGamesInSnapshot.has(key)) {
      // Expired, or the server confirmed the removal — stop tracking it.
      delete store[key];
      storeChanged = true;
      continue;
    }
    suppress.add(key);
  }
  if (storeChanged) writeStore(store);

  if (suppress.size === 0) return entries;
  return entries.filter(
    (entry) => !(entry.playerGuid === myGuid && suppress.has(String(entry.gameId)))
  );
}
